import { useRef, useState } from "react"

// tog'a #273 (2026-10-20, jonli dalil): «audio-react-recorder» 1.0.4 birinchi 2048 namunadagi jimjitlikni «mikrofon ishlamayapti»
// deb yozishni DARHOL to'xtatardi — bo'sh (44 bayt) WAV yuborilib, ikkala tomonda «Не удалось воспроизвести» chiqardi
// (telefonda mikrofon «uyg'onguncha» birinchi bo'lak ko'pincha nol). Endi standart MediaRecorder: yozib bo'lgach ovoz dekodlanib,
// 16 kHz mono 16-bit WAV ga aylantiriladi — iOS/Android/kompyuterda bir xil ijro etiladi (server va ijro qismi o'zgarmaydi).
export type VoiceState = "idle" | "recording" | "paused"

const MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"]
const TARGET_RATE = 16000

function toWav(buf: AudioBuffer): Blob {
  const ch = buf.numberOfChannels
  const src = buf.sampleRate
  const len = buf.length
  // mono
  const mono = new Float32Array(len)
  for (let c = 0; c < ch; c++) {
    const d = buf.getChannelData(c)
    for (let i = 0; i < len; i++) mono[i] += d[i] / ch
  }
  // 16 kHz ga (oddiy o'rtacha bilan)
  const ratio = src / TARGET_RATE
  const outLen = Math.max(1, Math.floor(len / ratio))
  const out = new Int16Array(outLen)
  for (let i = 0; i < outLen; i++) {
    const a = Math.floor(i * ratio), b = Math.min(len, Math.floor((i + 1) * ratio))
    let s = 0
    for (let j = a; j < b; j++) s += mono[j]
    const v = Math.max(-1, Math.min(1, b > a ? s / (b - a) : mono[a] || 0))
    out[i] = v < 0 ? v * 0x8000 : v * 0x7fff
  }
  const view = new DataView(new ArrayBuffer(44 + out.length * 2))
  const w = (o: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)) }
  w(0, "RIFF"); view.setUint32(4, 36 + out.length * 2, true); w(8, "WAVE"); w(12, "fmt ")
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true)
  view.setUint32(24, TARGET_RATE, true); view.setUint32(28, TARGET_RATE * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true)
  w(36, "data"); view.setUint32(40, out.length * 2, true)
  for (let i = 0; i < out.length; i++) view.setInt16(44 + i * 2, out[i], true)
  return new Blob([view], { type: "audio/wav" })
}

export default function useVoiceRecorder(onDone: (wav: Blob) => void, onError: (kind: "denied" | "unsupported" | "empty") => void) {
  const [state, setState] = useState<VoiceState>("idle")
  const rec = useRef<MediaRecorder | null>(null)
  const chunks = useRef<Blob[]>([])
  const cancelled = useRef(false)

  const start = async () => {
    if (state !== "idle") return
    if (!(navigator.mediaDevices && (window as any).MediaRecorder)) { onError("unsupported"); return }
    let stream: MediaStream
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }) } catch { onError("denied"); return }
    const mime = MIMES.find((m) => (window as any).MediaRecorder.isTypeSupported?.(m))
    const r: MediaRecorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream)
    chunks.current = []
    cancelled.current = false
    r.ondataavailable = (e) => { if (e.data && e.data.size) chunks.current.push(e.data) }
    r.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop())
      rec.current = null
      setState("idle")
      if (cancelled.current) return
      const raw = new Blob(chunks.current, { type: r.mimeType || mime || "audio/webm" })
      try {
        const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext
        const ctx = new Ctx()
        const decoded: AudioBuffer = await new Promise((ok, no) => {
          raw.arrayBuffer().then((ab) => { const p = ctx.decodeAudioData(ab, ok, no); if (p && p.catch) p.catch(no) }, no)
        })
        try { ctx.close && ctx.close() } catch {}
        if (decoded.duration < 0.3) { onError("empty"); return }
        onDone(toWav(decoded))
      } catch {
        onError("empty")
      }
    }
    rec.current = r
    r.start(250)
    setState("recording")
  }
  const pause = () => { if (rec.current?.state === "recording") { rec.current.pause(); setState("paused") } }
  const resume = () => { if (rec.current?.state === "paused") { rec.current.resume(); setState("recording") } }
  const stop = () => { if (rec.current && rec.current.state !== "inactive") rec.current.stop() }
  const cancel = () => { cancelled.current = true; stop() }

  return { state, start, pause, resume, stop, cancel }
}
